import {
  deleteField,
  doc,
  updateDoc,
  writeBatch,
} from 'firebase/firestore';
import type {
  DocumentData,
  QueryDocumentSnapshot,
  WriteBatch,
} from 'firebase/firestore';
import {
  attendeesCollectionRef,
  db,
  eventsCollectionRef,
} from '../firebase';
import { FIRESTORE_BATCH_LIMIT } from '../constants';
import {
  AuditActor,
  asFirestoreData,
  logActivity,
  stripSentinels,
} from '../audit-log';
import { diffFields } from '../diff-fields';
import {
  resolveAttendeePreference,
  type PreferenceResolution,
} from '../group-preference';
import { clearLabelUrlsForRefs } from './label-cache';
import type { BulkClearLabelsResult } from './label-cache';
import type { AppEvent, Attendee, Grade, Group } from '../types';

const eventLabel = (e: { name: string }): string => e.name;

// Creates an event in a single batched commit, optionally cloning the
// previous event's groups / rotations atomically via `buildClonesBatch`.
// The caller-owned batch pattern means a partial create can't leave a
// dangling event doc with no children.
export const createEvent = async (
  actor: AuditActor | null,
  newRef: string,
  payload: Record<string, unknown>,
  buildClonesBatch?: (batch: WriteBatch) => Promise<void>,
): Promise<void> => {
  const batch = writeBatch(db);
  batch.set(doc(eventsCollectionRef, newRef), payload);
  if (buildClonesBatch) await buildClonesBatch(batch);
  await batch.commit();

  const snapshot = stripSentinels(payload);
  logActivity({
    actor,
    entity: 'event',
    entityRef: newRef,
    entityLabel: eventLabel(snapshot as { name: string }),
    action: 'create',
    eventRef: newRef,
    snapshot,
    meta: buildClonesBatch ? { clonedChildren: true } : undefined,
  });
};

export const updateEvent = async (
  actor: AuditActor | null,
  before: AppEvent,
  payload: Record<string, unknown>,
): Promise<void> => {
  await updateDoc(
    doc(eventsCollectionRef, before.ref),
    asFirestoreData(payload),
  );
  const changes = diffFields(before, payload);
  if (Object.keys(changes).length === 0) return;
  logActivity({
    actor,
    entity: 'event',
    entityRef: before.ref,
    entityLabel: eventLabel(before),
    action: 'update',
    eventRef: before.ref,
    changes,
  });
};

// Promotes `target` to active while demoting the previously-active event
// in the same batch — keeps the "exactly one active event" invariant.
export const activateEvent = async (
  actor: AuditActor | null,
  target: AppEvent,
  previousActiveEvent: AppEvent | null,
): Promise<void> => {
  const batch = writeBatch(db);
  if (previousActiveEvent && previousActiveEvent.ref !== target.ref) {
    batch.update(doc(eventsCollectionRef, previousActiveEvent.ref), {
      isActive: false,
    });
  }
  batch.update(doc(eventsCollectionRef, target.ref), { isActive: true });
  await batch.commit();

  logActivity({
    actor,
    entity: 'event',
    entityRef: target.ref,
    entityLabel: eventLabel(target),
    action: 'activate',
    eventRef: target.ref,
    meta: { previousActiveEventRef: previousActiveEvent?.ref ?? null },
  });
};

export const archiveEvent = async (
  actor: AuditActor | null,
  target: AppEvent,
): Promise<void> => {
  await updateDoc(doc(eventsCollectionRef, target.ref), { isArchived: true });
  logActivity({
    actor,
    entity: 'event',
    entityRef: target.ref,
    entityLabel: eventLabel(target),
    action: 'archive',
    eventRef: target.ref,
  });
};

export const unarchiveEvent = async (
  actor: AuditActor | null,
  target: AppEvent,
): Promise<void> => {
  await updateDoc(doc(eventsCollectionRef, target.ref), {
    isArchived: deleteField(),
  });
  logActivity({
    actor,
    entity: 'event',
    entityRef: target.ref,
    entityLabel: eventLabel(target),
    action: 'unarchive',
    eventRef: target.ref,
  });
};

// -----------------------------------------------------------------------------
// Bulk operations on an event's children. These live with `events` because
// they're admin-only "wipe X for this event" actions logged against the
// event itself (not the individual attendee/group docs).
// -----------------------------------------------------------------------------

export const bulkDeleteAttendeesForEvent = async (
  actor: AuditActor | null,
  event: AppEvent,
  attendeeDocSnapshots: QueryDocumentSnapshot<DocumentData>[],
): Promise<number> => {
  const refs = attendeeDocSnapshots.map((d) => d.ref);

  for (let i = 0; i < refs.length; i += FIRESTORE_BATCH_LIMIT) {
    const batch = writeBatch(db);
    refs.slice(i, i + FIRESTORE_BATCH_LIMIT).forEach((r) => {
      batch.delete(r);
    });
    await batch.commit();
  }

  logActivity({
    actor,
    entity: 'event',
    entityRef: event.ref,
    entityLabel: event.name,
    action: 'bulk-delete',
    eventRef: event.ref,
    meta: { scope: 'attendees', count: refs.length },
  });
  return refs.length;
};

// Clears the cached `s3LabelUrl` on every supplied attendee then logs a
// single audit entry against the event. Callers pre-filter to attendees
// that actually have a cached URL (via `hasCachedLabel`) so the logged
// `count` reflects real writes instead of no-op `deleteField()` updates
// against absent fields. Empty input is a true no-op: no writes, no log
// entry. Audit log uses the cleared count (not the requested count), so
// the activity log reflects reality even when some batches fail; meta
// also carries `failed` when non-zero. Batching + per-batch error
// isolation live in `clearLabelUrlsForRefs`.
export const bulkClearLabelUrlsForEvent = async (
  actor: AuditActor | null,
  event: AppEvent,
  attendeeRefs: string[],
): Promise<BulkClearLabelsResult> => {
  const result = await clearLabelUrlsForRefs(
    attendeeRefs,
    'bulkClearLabelUrlsForEvent',
  );
  if (result.cleared > 0) {
    logActivity({
      actor,
      entity: 'event',
      entityRef: event.ref,
      entityLabel: event.name,
      action: 'clear-labels',
      eventRef: event.ref,
      meta:
        result.failed > 0
          ? { count: result.cleared, failed: result.failed }
          : { count: result.cleared },
    });
  }
  return result;
};

// Final M / F / unknown count of a single group after the algorithm
// finished — includes pre-existing members so the activity log shows the
// real post-run state, not just what was placed this run.
export type GroupGenderMix = {
  group: string;
  male: number;
  female: number;
  unknown: number;
};

export type AutoAssignResult = {
  assigned: number;
  skippedNoGrade: number;
  skippedNoGroup: number;
  perGrade: Record<
    string,
    { count: number; groups: string[]; genderMix: GroupGenderMix[] }
  >;
  // Number of cluster members the pre-pass stamped with
  // `group_preference_fulfilled: 'auto'`. Surfaces in the toast and the
  // `auto-assign` activity log entry's `meta`.
  fulfilledCount: number;
  // Largest |male - female| in any group across all touched grades after
  // the run. Surfaced in the toast (only when ≥ 2) and the activity log
  // so admins can spot a bad balance at a glance without digging into
  // `perGrade`. 0 means perfect balance everywhere.
  maxGenderSkew: number;
};

// Per-attendee placement decisions made by the cluster pre-pass within
// a single grade. Round-robin reads it to skip already-placed attendees
// and the stamping pass reads it to compare "where I ended up" against
// "where my matched targets ended up".
//
// `resolution` is optional because cluster mates can be silent: the BFS
// pulls in unassigned same-grade kids who are *named* by another kid
// even when they have no `group_preference` of their own. Those silent
// members must still travel with the cluster (one-directional prefs
// are the common case in practice), but they have nothing to stamp.
type ClusterPlacement = {
  groupRef: string;
  resolution?: PreferenceResolution;
};

/**
 * Distributes unassigned attendees across groups by grade_completed,
 * balancing gender so boys and girls are mixed across groups.
 *
 * Algorithm per grade:
 *   1. Cluster pre-pass: respect `group_preference` first (gender-blind by
 *      design — friend groups override gender mixing). Cluster placements
 *      bump the per-gender + total counts so the round-robin downstream
 *      sees their contribution.
 *   2. Separate the leftovers into boys, girls, unknown-gender buckets.
 *   3. Interleave boys/girls (larger bucket first) and walk the queue,
 *      placing each kid into the group with the lowest count for THAT
 *      kid's gender. Tiebreak by lowest total count, then group order.
 *      This is the fix for the old `i % N` round-robin which striped
 *      genders across groups when N was even or shared a factor with the
 *      alternation period.
 *   4. Unknown-gender attendees fill in at the end, targeting the groups
 *      with the fewest assignments so far (least-loaded by total count).
 *
 * Pre-existing assignments seed both the per-gender and total counts so
 * a re-run of auto-assign on a partially-populated grade compensates for
 * prior imbalance.
 *
 * Rejected kids: any attendee with `group_preference_fulfilled === 'rejected'`
 * is excluded from the cluster pre-pass entirely — neither as a source
 * nor as an anchor/target (edges into a rejected target are dropped).
 * They still get placed by the round-robin in step 2; their `'rejected'`
 * flag is preserved (the batch payload only sets `groupRef`).
 */
export const autoAssignAttendees = async (
  actor: AuditActor | null,
  unassigned: Attendee[],
  allAttendees: Attendee[],
  groups: Group[],
  event: AppEvent,
): Promise<AutoAssignResult> => {
  const eventRef = event.ref;
  const groupsByGrade = new Map<Grade, Group[]>();
  groups.forEach((g) => {
    const list = groupsByGrade.get(g.grade);
    if (list) list.push(g);
    else groupsByGrade.set(g.grade, [g]);
  });

  // `assignments` collects every write the algorithm wants to commit.
  // `fulfilledRefs` is the subset stamped with `'auto'` — written into
  // each assignment's payload below.
  const assignments: { attendeeRef: string; groupRef: string }[] = [];
  const fulfilledRefs = new Set<string>();
  let skippedNoGrade = 0;
  let skippedNoGroup = 0;
  const perGrade: AutoAssignResult['perGrade'] = {};
  let maxGenderSkew = 0;

  const attendeeByRef = new Map<string, Attendee>();
  for (const a of allAttendees) attendeeByRef.set(a.ref, a);

  const byGrade = new Map<Grade, Attendee[]>();
  for (const a of unassigned) {
    if (!a.groupRef) {
      if (!a.grade_completed) {
        skippedNoGrade++;
        continue;
      }
      const list = byGrade.get(a.grade_completed);
      if (list) list.push(a);
      else byGrade.set(a.grade_completed, [a]);
    }
  }

  const gradeEntries = Array.from(byGrade.entries());
  for (const [grade, gradeAttendees] of gradeEntries) {
    const gradeGroups = groupsByGrade.get(grade);
    if (!gradeGroups || gradeGroups.length === 0) {
      skippedNoGroup += gradeAttendees.length;
      continue;
    }

    // Seed the per-group counts with existing assignments so every
    // placement decision (cluster pre-pass tiebreaks, gender-balanced
    // leftover round-robin, unknown-gender fallback) sees the real
    // current load. We track total + per-gender separately because the
    // round-robin picks the least-loaded group of THIS attendee's gender
    // and only tiebreaks on total count.
    const counts = new Map<string, number>();
    const boyCounts = new Map<string, number>();
    const girlCounts = new Map<string, number>();
    gradeGroups.forEach((g) => {
      counts.set(g.ref, 0);
      boyCounts.set(g.ref, 0);
      girlCounts.set(g.ref, 0);
    });
    for (const a of allAttendees) {
      if (
        a.grade_completed === grade &&
        a.groupRef &&
        counts.has(a.groupRef)
      ) {
        counts.set(a.groupRef, (counts.get(a.groupRef) ?? 0) + 1);
        if (a.gender === 'Male') {
          boyCounts.set(a.groupRef, (boyCounts.get(a.groupRef) ?? 0) + 1);
        } else if (a.gender === 'Female') {
          girlCounts.set(a.groupRef, (girlCounts.get(a.groupRef) ?? 0) + 1);
        }
      }
    }

    // --- Cluster pre-pass --------------------------------------------------
    // Resolve preferences for every unassigned-in-this-grade kid that
    // hasn't been admin-rejected. Only those whose resolution produced
    // ≥1 same-grade `matched` ref enter the cluster graph; the rest
    // fall through to the round-robin. Rejected kids are skipped here
    // by design (admin already decided this isn't a preference-driven
    // placement) and get round-robined like a no-preference kid below.
    const resolutions = new Map<string, PreferenceResolution>();
    for (const a of gradeAttendees) {
      if (a.group_preference_fulfilled === 'rejected') continue;
      const r = resolveAttendeePreference(a, allAttendees);
      if (r.matchedRefs.length > 0) resolutions.set(a.ref, r);
    }

    // Build adjacency graph (undirected). Nodes are the source attendees
    // plus all their matched targets — including already-assigned anchor
    // attendees, which let an unassigned cluster member follow an anchor
    // into its group.
    const adj = new Map<string, Set<string>>();
    const addEdge = (u: string, v: string) => {
      let set = adj.get(u);
      if (!set) {
        set = new Set();
        adj.set(u, set);
      }
      set.add(v);
    };
    // For each source attendee, draw an undirected edge to each matched
    // target. The reverse edge intentionally drags `m` into the cluster
    // even when `m` is silent (no `group_preference` of their own) or
    // already assigned (an anchor) — both cases are essential for the
    // pre-pass to move the whole cluster together.
    Array.from(resolutions.entries()).forEach(([ref, r]) => {
      for (const m of r.matchedRefs) {
        // Edges into a rejected target are dropped: a rejected kid
        // (already in a group or unassigned) doesn't pull non-rejected
        // kids toward them via preference edges. The non-rejected kid
        // round-robins normally.
        const target = attendeeByRef.get(m);
        if (target?.group_preference_fulfilled === 'rejected') continue;
        addEdge(ref, m);
        addEdge(m, ref);
      }
    });

    // Connected components (BFS).
    const visited = new Set<string>();
    const clusters: string[][] = [];
    const adjKeys = Array.from(adj.keys());
    for (const node of adjKeys) {
      if (visited.has(node)) continue;
      visited.add(node);
      const cluster: string[] = [];
      const queue: string[] = [node];
      while (queue.length > 0) {
        const n = queue.shift() as string;
        cluster.push(n);
        const neighbors = adj.get(n);
        if (neighbors) {
          neighbors.forEach((nb) => {
            if (!visited.has(nb)) {
              visited.add(nb);
              queue.push(nb);
            }
          });
        }
      }
      clusters.push(cluster);
    }

    // Placement decisions for this grade's cluster members. Keyed by
    // attendee ref. Only includes unassigned attendees the pre-pass
    // actually placed.
    const placedByCluster = new Map<string, ClusterPlacement>();

    for (const cluster of clusters) {
      // Anchor groups: every distinct `groupRef` already held by any
      // cluster member (anchors live in `allAttendees`). Counts let the
      // conflict tiebreak find the busier-but-still-targeted group.
      const anchorGroups = new Map<string, number>();
      for (const ref of cluster) {
        const a = attendeeByRef.get(ref);
        if (a?.groupRef && counts.has(a.groupRef)) {
          anchorGroups.set(
            a.groupRef,
            (anchorGroups.get(a.groupRef) ?? 0) + 1,
          );
        }
      }

      const unassignedInCluster = cluster.filter((ref) => {
        const a = attendeeByRef.get(ref);
        return Boolean(a && !a.groupRef && a.grade_completed === grade);
      });
      if (unassignedInCluster.length === 0) continue;

      const place = (ref: string, groupRef: string) => {
        // Silent cluster mates (named-but-no-preference) have no entry
        // in `resolutions`; they still get placed so the cluster moves
        // as one. The stamping pass below skips them.
        placedByCluster.set(ref, { groupRef, resolution: resolutions.get(ref) });
        counts.set(groupRef, (counts.get(groupRef) ?? 0) + 1);
        // Bump per-gender so the round-robin downstream knows this
        // group already has a cluster member of this gender and can
        // compensate when distributing leftovers.
        const placed = attendeeByRef.get(ref);
        if (placed?.gender === 'Male') {
          boyCounts.set(groupRef, (boyCounts.get(groupRef) ?? 0) + 1);
        } else if (placed?.gender === 'Female') {
          girlCounts.set(groupRef, (girlCounts.get(groupRef) ?? 0) + 1);
        }
      };

      if (anchorGroups.size === 1) {
        const target = Array.from(anchorGroups.keys())[0];
        for (const ref of unassignedInCluster) place(ref, target);
      } else if (anchorGroups.size > 1) {
        // Conflict: place each unassigned member in the anchor group
        // they're most tied to (most resolved fragments pointing into
        // it). Ties broken by lower current count.
        for (const ref of unassignedInCluster) {
          const ties = new Map<string, number>();
          const r = resolutions.get(ref);
          if (r) {
            for (const m of r.matchedRefs) {
              const target = attendeeByRef.get(m);
              const grp =
                target?.groupRef ?? placedByCluster.get(m)?.groupRef;
              if (grp && anchorGroups.has(grp)) {
                ties.set(grp, (ties.get(grp) ?? 0) + 1);
              }
            }
          }
          let best: string | undefined;
          let bestTies = -1;
          let bestCount = Number.POSITIVE_INFINITY;
          Array.from(anchorGroups.keys()).forEach((grp) => {
            const t = ties.get(grp) ?? 0;
            const c = counts.get(grp) ?? 0;
            if (t > bestTies || (t === bestTies && c < bestCount)) {
              best = grp;
              bestTies = t;
              bestCount = c;
            }
          });
          if (best) place(ref, best);
        }
      } else {
        // No anchor: pick this grade's least-loaded group and put the
        // whole cluster there.
        let target = gradeGroups[0].ref;
        let min = counts.get(target) ?? 0;
        for (const g of gradeGroups) {
          const c = counts.get(g.ref) ?? 0;
          if (c < min) {
            target = g.ref;
            min = c;
          }
        }
        for (const ref of unassignedInCluster) place(ref, target);
      }
    }

    // Stamp `'auto'` for cluster members whose every `matched` fragment
    // resolves to the same group they just landed in. Anchors live in
    // `allAttendees`; freshly-placed cluster mates live in
    // `placedByCluster`. Non-matched fragments (not-found / cross-
    // grade / ambiguous / incomplete) are ignored — only `matched`
    // counts here, by design. Silent cluster mates (no resolution, or
    // empty `matchedRefs`) have no preference to fulfill and are skipped
    // — without this guard `Array.every` would vacuously return true and
    // wrongly stamp them.
    Array.from(placedByCluster.entries()).forEach(
      ([ref, { groupRef, resolution }]) => {
        if (!resolution || resolution.matchedRefs.length === 0) return;
        const allTogether = resolution.matchedRefs.every((m: string) => {
          const target = attendeeByRef.get(m);
          const grp = target?.groupRef ?? placedByCluster.get(m)?.groupRef;
          return grp === groupRef;
        });
        if (allTogether) fulfilledRefs.add(ref);
      },
    );

    // Materialize cluster placements into the assignments list.
    Array.from(placedByCluster.entries()).forEach(([ref, { groupRef }]) => {
      assignments.push({ attendeeRef: ref, groupRef });
    });

    // --- Gender-balanced leftover round-robin -----------------------------
    const leftovers = gradeAttendees.filter((a) => !placedByCluster.has(a.ref));

    const boys = leftovers.filter((a: Attendee) => a.gender === 'Male');
    const girls = leftovers.filter((a: Attendee) => a.gender === 'Female');
    const unknown = leftovers.filter((a: Attendee) => !a.gender);

    // Walk in interleaved order so when boys and girls counts are equal
    // across all groups (the common starting state), we still hand out
    // alternating B/G picks rather than dumping all boys first. The
    // larger bucket leads so the tail end is the over-represented gender.
    const interleaved: Attendee[] = [];
    const [larger, smaller] =
      boys.length >= girls.length ? [boys, girls] : [girls, boys];
    let li = 0;
    let si = 0;
    while (li < larger.length || si < smaller.length) {
      if (li < larger.length) interleaved.push(larger[li++]);
      if (si < smaller.length) interleaved.push(smaller[si++]);
    }

    // Pick the group with the lowest count for THIS attendee's gender.
    // Tiebreak by total count, then by group order (the first group
    // encountered wins on a full tie). For unknown gender (handled
    // below) we ignore the per-gender axis and just use total count.
    const pickGroup = (gender: Attendee['gender']): string => {
      const genderCounts =
        gender === 'Male' ? boyCounts : gender === 'Female' ? girlCounts : null;
      let best = gradeGroups[0].ref;
      let bestGender = genderCounts?.get(best) ?? 0;
      let bestTotal = counts.get(best) ?? 0;
      for (let i = 1; i < gradeGroups.length; i++) {
        const ref = gradeGroups[i].ref;
        const gCount = genderCounts?.get(ref) ?? 0;
        const tCount = counts.get(ref) ?? 0;
        if (
          gCount < bestGender ||
          (gCount === bestGender && tCount < bestTotal)
        ) {
          best = ref;
          bestGender = gCount;
          bestTotal = tCount;
        }
      }
      return best;
    };

    for (const a of interleaved) {
      const groupRef = pickGroup(a.gender);
      assignments.push({ attendeeRef: a.ref, groupRef });
      counts.set(groupRef, (counts.get(groupRef) ?? 0) + 1);
      if (a.gender === 'Male') {
        boyCounts.set(groupRef, (boyCounts.get(groupRef) ?? 0) + 1);
      } else if (a.gender === 'Female') {
        girlCounts.set(groupRef, (girlCounts.get(groupRef) ?? 0) + 1);
      }
    }

    for (const a of unknown) {
      const groupRef = pickGroup(undefined);
      assignments.push({ attendeeRef: a.ref, groupRef });
      counts.set(groupRef, (counts.get(groupRef) ?? 0) + 1);
    }

    // Snapshot the post-run mix per group. `boyCounts` / `girlCounts`
    // include both pre-existing members (seeded above) and everyone the
    // algorithm placed this run, so this is the actual final state of
    // each group — what an admin sees on the Roster after auto-assign.
    const genderMix: GroupGenderMix[] = gradeGroups.map((g) => {
      const male = boyCounts.get(g.ref) ?? 0;
      const female = girlCounts.get(g.ref) ?? 0;
      const total = counts.get(g.ref) ?? 0;
      return {
        group: g.groupName,
        male,
        female,
        unknown: Math.max(0, total - male - female),
      };
    });
    for (const mix of genderMix) {
      const skew = Math.abs(mix.male - mix.female);
      if (skew > maxGenderSkew) maxGenderSkew = skew;
    }

    perGrade[grade as string] = {
      count: gradeAttendees.length,
      groups: gradeGroups.map((g) => g.groupName),
      genderMix,
    };
  }

  // Clear `s3LabelUrl` on the same update so a stale cached label (e.g. a
  // pre-assignment print) doesn't survive into the group the attendee was
  // just placed in. Matches the cache-invalidation policy on every other
  // groupRef mutation path (`updateAttendee`, `deleteGroupWithReassignment`).
  for (let i = 0; i < assignments.length; i += FIRESTORE_BATCH_LIMIT) {
    const batch = writeBatch(db);
    assignments
      .slice(i, i + FIRESTORE_BATCH_LIMIT)
      .forEach(({ attendeeRef, groupRef }) => {
        const payload: Record<string, unknown> = {
          groupRef,
          s3LabelUrl: deleteField(),
        };
        if (fulfilledRefs.has(attendeeRef)) {
          payload.group_preference_fulfilled = 'auto';
        }
        batch.update(
          doc(attendeesCollectionRef, attendeeRef),
          asFirestoreData(payload),
        );
      });
    await batch.commit();
  }

  if (assignments.length > 0) {
    logActivity({
      actor,
      entity: 'event',
      entityRef: eventRef,
      entityLabel: event.name,
      action: 'auto-assign',
      eventRef,
      meta: {
        count: assignments.length,
        skippedNoGrade,
        skippedNoGroup,
        perGrade,
        fulfilledCount: fulfilledRefs.size,
        maxGenderSkew,
      },
    });
  }

  return {
    assigned: assignments.length,
    skippedNoGrade,
    skippedNoGroup,
    perGrade,
    fulfilledCount: fulfilledRefs.size,
    maxGenderSkew,
  };
};
